import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FadeImage } from '../motion/FadeImage';

const SERVICES = [
    {
        n: '01',
        room: 'Stairwell',
        title: 'Interior Painting',
        desc:
            'Editorial-grade wall and ceiling finishes across whole-home repaints, single rooms and detail trim. Meticulous prep, zero splatter, precise cut-lines.',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/nv30hvci_IMG_5198.webp',
        clip: 'clip-notch-tr',
    },
    {
        n: '02',
        room: 'Cabinets',
        title: 'Cabinet Refinishing',
        desc:
            'Full-scope cabinet strip, sand, prime and sprayed lacquer or 2K polyurethane. Furniture-grade finish, hardware upgrades, on-site colour development.',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/ogmgcc6s_83563EAA-03CB-44FC-94C0-9E04ABF8B664.webp',
        clip: 'clip-notch-bl',
    },
    {
        n: '03',
        room: 'Facade',
        title: 'Stucco Refinishing',
        desc:
            'New Mexico stucco takes a beating from sun and wind. We patch, prime and refinish with mineral-silicate and elastomeric systems tuned for the high desert — colour-matched, weather-tight, and quietly beautiful.',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/nclzq6xe_Screenshot%202026-07-02%20at%2010.40.40%E2%80%AFAM.png',
        clip: 'clip-arch',
    },
    {
        n: '04',
        room: 'Exterior',
        title: 'Exterior Painting',
        desc:
            'Southwestern climates are cruel to paint. We spec elastomeric and mineral silicate systems tuned for New Mexico UV, wind and thermal cycles.',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/322qbedy_Screenshot%202026-07-02%20at%2010.39.59%E2%80%AFAM.png',
        clip: 'clip-notch-tr',
    },
    {
        n: '05',
        room: 'Complex',
        title: 'Commercial Services',
        desc:
            'After-hours execution for retail, offices, medical and multifamily. Certified crews, scheduled progressions, meticulous protection of tenanted spaces.',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/ses3ug2d_Screenshot%202026-07-02%20at%2010.40.25%E2%80%AFAM.png',
        clip: 'clip-notch-bl',
    },
];

const ServiceRow = ({ s, index }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.25 });
    const [hover, setHover] = useState(false);
    const navigate = useNavigate();
    const right = index % 2 === 1;

    // Map service.title → modal radio id
    const serviceKey = (() => {
        const t = s.title.toLowerCase();
        if (t.includes('interior')) return 'interior';
        if (t.includes('exterior')) return 'exterior';
        if (t.includes('cabinet')) return 'cabinets';
        if (t.includes('stucco')) return 'stucco';
        if (t.includes('commercial')) return 'commercial';
        return '';
    })();
    return (
        <div
            ref={ref}
            className="relative grid grid-cols-12 gap-6 md:gap-10 py-20 md:py-32 border-t border-white/10 group"
            data-testid={`service-row-${s.n}`}
        >
            {/* Text side */}
            <div className={`col-span-12 md:col-span-6 ${right ? 'md:order-2 md:pl-8' : ''} flex flex-col justify-center`}>
                <div className="eyebrow text-bone-400 mb-4">Chapter 05 · {s.room} — {s.n}</div>
                <motion.h3
                    initial={{ opacity: 0, y: 40 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1.1, ease: [0.7, 0, 0.2, 1] }}
                    className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-[0.98] tracking-tight text-bone-100"
                >
                    {s.title}
                </motion.h3>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="mt-6 max-w-md font-body text-base md:text-lg font-light text-bone-300 leading-relaxed"
                >
                    {s.desc}
                </motion.p>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : {}}
                    transition={{ duration: 1, delay: 0.35 }}
                    className="mt-8"
                >
                    <button
                        type="button"
                        onClick={() => navigate(`/estimate?service=${encodeURIComponent(serviceKey)}&details=${encodeURIComponent(`Interested in ${s.title}`)}`)}
                        className="link-underline inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.28em] text-adobe-500 hover:text-adobe-300"
                        data-testid={`service-cta-${s.n}`}
                    >
                        Enquire about {s.room.toLowerCase()}
                        <ArrowUpRight size={14} strokeWidth={1.4} />
                    </button>
                </motion.div>
            </div>

            {/* Image side */}
            <motion.div
                initial={{ opacity: 0, y: 60, scale: 0.98 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 1.4, ease: [0.7, 0, 0.2, 1] }}
                className={`col-span-12 md:col-span-6 ${right ? 'md:order-1' : ''}`}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                <div className={`relative overflow-hidden ${s.clip} bg-ink-800`}>
                    <div className="aspect-[4/5] md:aspect-[5/6] relative">
                        <FadeImage
                            src={s.image}
                            alt={s.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.7,0,0.2,1)]"
                            style={{ transform: hover ? 'scale(1.06)' : 'scale(1)' }}
                        />
                        {/* Spotlight vignette */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    'radial-gradient(circle at 30% 30%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.35) 80%)',
                            }}
                        />
                        {/* Frame corner ID */}
                        <div className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 text-bone-100">
                            <span className="w-6 h-px bg-adobe-500" />
                            <span className="font-mono text-[10px] tracking-[0.32em] uppercase">Room {s.n}</span>
                        </div>
                        {/* Hover reveal */}
                        <div
                            className="absolute inset-0 flex items-end p-6 md:p-8 pointer-events-none transition-opacity duration-500"
                            style={{ opacity: hover ? 1 : 0 }}
                        >
                            <div className="font-display text-2xl md:text-3xl text-bone-100">
                                {s.room} → {s.title}
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export const Services = () => {
    return (
        <section
            id="services"
            data-testid="msd-services"
            className="relative z-20 bg-ink-900 px-6 md:px-10"
        >
            <div className="mx-auto max-w-[1600px]">
                <div className="pt-24 md:pt-32 pb-4 grid grid-cols-12">
                    <div className="col-span-12 md:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-px bg-adobe-500" />
                            <span className="eyebrow">Services · Room by room</span>
                        </div>
                        <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-bone-100">
                            Walk the home.
                            <br />
                            <span className="italic text-bone-300">Every room, our craft.</span>
                        </h2>
                    </div>
                    <div className="hidden md:flex md:col-span-6 items-end justify-end">
                        <div className="max-w-sm text-right">
                            <div className="font-body text-sm font-light text-bone-400 leading-relaxed">
                                Instead of a menu of services, follow us through a house. Each room reveals a discipline — the surface, the technique, the material.
                            </div>
                        </div>
                    </div>
                </div>

                {SERVICES.map((s, i) => <ServiceRow s={s} index={i} key={s.n} />)}
            </div>
        </section>
    );
};
